import React, { useState, useEffect } from 'react';
import { benchActivityService, MyActivityTaskDto } from '../services/benchActivityService';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { CalendarToday } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { CheckCircle, Edit, Visibility, DeleteOutline } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';

// Helper function to validate date (only allow dates from calendar)
// Only allow dates that are present in the calendar (e.g., in the allowed week)
function isValidDate(date: string | dayjs.Dayjs | null): boolean {
  if (!date) return false;
  const regex = /^\d{2}-[A-Za-z]{3}-\d{4}$/;
  let d: dayjs.Dayjs;
  if (typeof date === 'string') {
    if (!regex.test(date)) return false;
    d = dayjs(date, 'DD-MMM-YYYY');
  } else {
    d = dayjs(date);
  }
  if (!d.isValid()) return false;
  // Get week start and end from activity.week (assume it's Monday)
  // Find activityWeekStr from props or closure
  const modal = document.querySelector('[data-activity-week]');
  let weekStr = modal?.getAttribute('data-activity-week');
  if (!weekStr) return false;
  // Accept both DD-MMM-YYYY and ISO
  let weekStart = dayjs(weekStr, ['DD-MMM-YYYY', 'YYYY-MM-DD']);
  if (!weekStart.isValid()) return false;
  // Week is Monday to Sunday
  const weekEnd = weekStart.add(6, 'day');
  // Use isSame, isAfter, isBefore for compatibility
  const isAfterOrSame = d.isAfter(weekStart, 'day') || d.isSame(weekStart, 'day');
  const isBeforeOrSame = d.isBefore(weekEnd, 'day') || d.isSame(weekEnd, 'day');
  return isAfterOrSame && isBeforeOrSame;
}



// Define ActivityRow type before use
type ActivityRow = {
  taskId?: number;
  date: string | Dayjs | null;
  comments: string;
  hours: string;
};

interface ActivityData {
  week: string;
  weekDisplay?: string;
  category: string;
  technology: string;
  name: string;
  description?: string;
  status: string;
  engagementId: number;
  activityId:number;
  startDate?: string;
}

interface UpdateActivityModalProps {
  open: boolean;
  onClose: () => void;
  activity: ActivityData;
  onSave: (data: any) => void;
  initialRows?: ActivityRow[];
}

export default function UpdateActivityModal({
  open,
  onClose,
  activity,
  onSave,
  initialRows = [],
}: UpdateActivityModalProps) {

  // Calendar popper state (must be at top of function)
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarIdx, setCalendarIdx] = useState<number | null>(null);
  const [calendarType, setCalendarType] = useState<'unsaved' | 'edit' | null>(null);
  const [pendingDate, setPendingDate] = useState<string | null>(null);
  const getInitialStatus = (s: string | undefined) => {
    if (!s || s === 'yet to start') return 'In Progress';
    return s;
  };
  const [status, setStatus] = useState(getInitialStatus(activity.status));

  // Reset status to activity status when modal opens or activity changes
  useEffect(() => {
    if (open) {
      setStatus(getInitialStatus(activity.status));
    }
  }, [open, activity.status]);
  const [statusHistory, setStatusHistory] = useState(activity.status ? [activity.status] : ['yet to start']);
  const [startDate, setStartDate] = useState(activity.startDate || '');

  // Persisted records (ticked rows)
  const [rows, setRows] = useState<ActivityRow[]>(initialRows);
  useEffect(() => {
    if (open && activity.engagementId) {
      benchActivityService
        .getTasksByEngagementId(activity.engagementId)
        .then((res: any) => {
          // handle both array and {data:[]}
          const tasks = Array.isArray(res) ? res : res?.data || [];
          setRows(
            tasks
              .map((t: any) => ({
                taskId: t.taskID,
                date: t.taskDate ? dayjs(t.taskDate).format('DD-MMM-YYYY') : '',
                comments: t.comments || '',
                hours: t.hours ? t.hours.toString() : ''
              }))
              .sort((a: ActivityRow, b: ActivityRow) => {
                if (!a.date) return -1;
                if (!b.date) return 1;
                const da = typeof a.date === 'string' ? dayjs(a.date, 'DD-MMM-YYYY') : a.date;
                const db = typeof b.date === 'string' ? dayjs(b.date, 'DD-MMM-YYYY') : b.date;
                return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
              })
          );
        })
        .catch((err: any) => {
          console.error("Error fetching tasks:", err);
          setRows([]);
        });
    }
    // Reset edit mode and discard unsaved edits when modal is closed or reopened
    if (!open) {
      setEditingIdx(null);
      setEditRowsMap({});
    }
  }, [open, activity.engagementId]);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  // Store unsaved edit rows per activity (keyed by activity.name)
  const [editRowsMap, setEditRowsMap] = useState<{ [key: string]: ActivityRow[] }>({});
  const activityKey = activity.name;
  const editRows = editRowsMap[activityKey] || [];
  const setEditRows: React.Dispatch<React.SetStateAction<ActivityRow[]>> = (valueOrUpdater) => {
    setEditRowsMap(prev => {
      const prevRows = prev[activityKey] || [];
      const newRows = typeof valueOrUpdater === 'function' ? (valueOrUpdater as (prev: ActivityRow[]) => ActivityRow[])(prevRows) : valueOrUpdater;
      return { ...prev, [activityKey]: newRows };
    });
  };

  // Error state for hours validation
  const [hoursErrorIdx, setHoursErrorIdx] = useState<number | null>(null);
  const [hoursErrorMsg, setHoursErrorMsg] = useState<string>("");
  // Error state for comments validation
  const [commentsErrorIdx, setCommentsErrorIdx] = useState<number | null>(null);
  // Global error for hours (for below the form)
  const [globalHoursError, setGlobalHoursError] = useState<string>("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

const handleStatusChange = (e: SelectChangeEvent<string>) => {
  const newStatus = e.target.value;
  // Prevent reverting from 'in progress' or 'completed' back to 'yet to start'
  if ((status === 'In Progress' || status === 'completed') && newStatus === 'yet to start') {
    setSnackbar({ open: true, message: 'Cannot revert to "Yet to Start" once progressed.', severity: 'error' });
    return;
  }
  setStatus(newStatus);
  setStatusHistory(prev => [...prev, newStatus]);
  if (status === "yet to start" && newStatus === "In Progress" && !startDate) {
    setStartDate(new Date().toISOString());
  }
};

  /* ---------------- ADD ROW (ALLOW MULTIPLE UNSAVED) ---------------- */
  // Add a new unsaved row (not persisted until ticked)
  const [unsavedRows, setUnsavedRows] = useState<ActivityRow[]>([]);
  // Track if user tried to add/save unsaved row (for error display)
  const [unsavedDateTouched, setUnsavedDateTouched] = useState<boolean[]>([]);
  // Track if user tried to save an edit row (for error display)
  const [editDateTouched, setEditDateTouched] = useState<{ [idx: number]: boolean }>({});

  // Reset unsavedRows when modal is opened or activity changes
  useEffect(() => {
    if (open) {
      setUnsavedRows([]);
      setUnsavedDateTouched([]);
    }
  }, [open, activity.engagementId]);
  const handleAddRow = async () => {
    // If there is an unsaved row, try to save it first
    if (unsavedRows.length > 0) {
      const idx = 0; // always only one unsaved row at the bottom
      const row = unsavedRows[0];
      // Validate all fields
      if (!row.date || !row.comments || row.hours === undefined || row.hours === null || row.hours === '' || row.comments.trim().length === 0) {
        const isDateEmpty = !row.date;
        const isCommentsEmpty = !row.comments || row.comments.trim().length === 0;
        const isHoursEmpty = row.hours === undefined || row.hours === null || row.hours === '';
        if (!isDateEmpty && isCommentsEmpty && !isHoursEmpty) {
          setCommentsErrorIdx(idx);
          setUnsavedDateTouched([true]);
          setSnackbar({
            open: true,
            message: 'Comments field cannot be empty',
            severity: 'error',
          });
        } else {
          setCommentsErrorIdx(null);
          setUnsavedDateTouched([true]);
          setSnackbar({
            open: true,
            message: 'All the fields are mandatory',
            severity: 'error',
          });
        }
        return;
      }
      if (!isValidDate(row.date)) {
        setUnsavedDateTouched([true]);
        setSnackbar({
          open: true,
          message: 'Date must be within the current activity week.',
          severity: 'error',
        });
        return;
      }
      let hoursNum = Number(row.hours);
      let isValidHours = row.hours === '' || /^([0-9]{1,2}|0)(\.[0-9]{1,2})?$/.test(row.hours);
      if (row.hours !== '' && (
        isNaN(hoursNum) ||
        hoursNum < 0.01 ||
        hoursNum > 10.0 ||
        !isValidHours
      )) {
        setHoursErrorIdx(idx);
        setHoursErrorMsg('Please enter a value between 0.01 and 10.0 with up to 2 digits before and after the decimal.');
        setGlobalHoursError('Please enter a value between 0.01 and 10.0 with up to 2 digits before and after the decimal.');
        return;
      } else {
        setHoursErrorIdx(null);
        setHoursErrorMsg("");
        setGlobalHoursError("");
      }
      setCommentsErrorIdx(null);
      try {
        if (!activity.engagementId) throw new Error('No engagementId');
        // Prevent duplicate date
        if (rows.some(r => r.date === row.date)) {
          setSnackbar({ open: true, message: 'Record already exists', severity: 'error' });
          return;
        }
        const taskDto = {
          taskDate:
            typeof row.date === 'string'
              ? dayjs(row.date, ['YYYY-MM-DD', 'DD-MMM-YYYY']).format('YYYY-MM-DD')
              : row.date
                ? row.date.format('YYYY-MM-DD')
                : '',
          comments: row.comments,
          hours: row.hours,
          status: status
        };
        await benchActivityService.addTask(activity.engagementId, taskDto);
        // Refresh rows from backend
        const tasks = await benchActivityService.getTasksByEngagementId(activity.engagementId);
        const taskArray: any[] = Array.isArray(tasks)
          ? tasks
          : (tasks && typeof tasks === 'object' && 'data' in tasks && Array.isArray((tasks as any).data))
            ? (tasks as any).data
            : [];
        setRows(taskArray.map((t: any) => ({
          taskId: t.taskID,
          date: t.taskDate ? dayjs(t.taskDate).format('DD-MMM-YYYY') : '',
          comments: t.comments,
          hours: t.hours?.toString() || ''
        })).sort((a: ActivityRow, b: ActivityRow) => {
          if (!a.date) return -1;
          if (!b.date) return 1;
          const da = typeof a.date === 'string' ? dayjs(a.date, 'DD-MMM-YYYY') : a.date;
          const db = typeof b.date === 'string' ? dayjs(b.date, 'DD-MMM-YYYY') : b.date;
          return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
        }));
        setSnackbar({ open: true, message: 'Saved!', severity: 'success' });
        setUnsavedRows([]);
      } catch (err) {
        let msg = 'Error';
        if (err && typeof err === 'object') {
          const e = err as any;
          msg = e?.response?.data?.message || e?.message || 'Error';
        }
        setSnackbar({
          open: true,
          message: msg,
          severity: 'error'
        });
        return;
      }
    }
    // Add a new empty unsaved row
    setUnsavedRows([{ date: null, comments: '', hours: '' }]);
    setUnsavedDateTouched([false]);
    setHoursErrorIdx(null);
    setHoursErrorMsg("");
  };

  /* ---------------- EDIT EXISTING ROW ---------------- */
  const handleEditRow = (idx: number) => {
    setEditingIdx(idx);
  };

  /* ---------------- SAVE SINGLE EDIT ROW ---------------- */
  // Save ticked row: move from unsavedRows to rows, or update existing row
    const handleSaveEditRow = async (idx: number, updatedRow: ActivityRow, isUnsaved: boolean = false) => {
    // In handleSaveEditRow, set editDateTouched for this idx if invalid
    const handleSaveEditRow = async (idx: number, updatedRow: ActivityRow, isUnsaved: boolean = false) => {
      if (isUnsaved) {
        setUnsavedDateTouched(touched => {
          const arr = [...touched];
          arr[idx] = true;
          return arr;
        });
      } else {
        setEditDateTouched(touched => ({ ...touched, [idx]: true }));
      }
              setSnackbar({
                open: true,
                message: 'Cannot save task when status is "Yet to Start".',
                severity: 'error',
              });
              return;
            }
      if (!updatedRow.date || !updatedRow.comments || updatedRow.hours === undefined || updatedRow.hours === null || updatedRow.hours === '' || updatedRow.comments.trim().length === 0) {
        const isEditDateEmpty = !updatedRow.date;
        const isEditCommentsEmpty = !updatedRow.comments || updatedRow.comments.trim().length === 0;
        const isEditHoursEmpty = updatedRow.hours === undefined || updatedRow.hours === null || updatedRow.hours === '';
        if (!isEditDateEmpty && isEditCommentsEmpty && !isEditHoursEmpty) {
          setCommentsErrorIdx(idx);
          setSnackbar({
            open: true,
            message: 'Comments field cannot be empty',
            severity: 'error',
          });
        } else {
          setCommentsErrorIdx(null);
          setSnackbar({
            open: true,
            message: 'All the fields are mandatory',
            severity: 'error',
          });
        }
        return;
      }
      setCommentsErrorIdx(null);
      // Validate date is within allowed week
      if (!isValidDate(updatedRow.date)) {
        setSnackbar({
          open: true,
          message: 'Date must be within the current activity week.',
          severity: 'error',
        });
        return;
      }
      // Validate hours: 1 or 2 digits before and after decimal, in range 0.01 to 10.0
      let hoursNum = Number(updatedRow.hours);
      let isValidHours = updatedRow.hours === '' || /^([0-9]{1,2}|0)(\.[0-9]{1,2})?$/.test(updatedRow.hours);
      if (updatedRow.hours !== '' && (
        isNaN(hoursNum) ||
        hoursNum < 0.01 ||
        hoursNum > 10.0 ||
        !isValidHours
      )) {
        setHoursErrorIdx(idx);
        setHoursErrorMsg('Please enter a value between 0.01 and 10.0 with up to 2 digits before and after the decimal.');
        setGlobalHoursError('Please enter a value between 0.01 and 10.0 with up to 2 digits before and after the decimal.');
        return;
      } else {
        setHoursErrorIdx(null);
        setHoursErrorMsg("");
        setGlobalHoursError("");
      }

        try {
            if (!activity.engagementId) throw new Error('No engagementId');

            // Prevent duplicate date
            if (rows.some((r, i) => i !== idx && r.date === updatedRow.date)) {
                setSnackbar({ open: true, message: 'Record already exists', severity: 'error' });
                return;
            }

            const taskDto = {
              taskDate:
                typeof updatedRow.date === 'string'
                  ? dayjs(updatedRow.date, ['YYYY-MM-DD', 'DD-MMM-YYYY']).format('YYYY-MM-DD')
                  : updatedRow.date
                    ? updatedRow.date.format('YYYY-MM-DD')
                    : '',
              comments: updatedRow.comments,
                hours: updatedRow.hours,
                status: status
            };

            if (updatedRow.taskId) {
                await benchActivityService.updateTask(updatedRow.taskId, activity.engagementId, taskDto);
            } else {
                await benchActivityService.addTask(activity.engagementId, taskDto);
            }

            // 🔥 FIX HERE (cast as any)
            const tasks: any = await benchActivityService.getTasksByEngagementId(activity.engagementId);

            setRows(tasks.map((t: any) => ({
                taskId: t.taskID,
                date: t.taskDate ? dayjs(t.taskDate).format('DD-MMM-YYYY') : '',
                comments: t.comments,
                hours: t.hours?.toString() || ''
              }))
              .sort((a: ActivityRow, b: ActivityRow) => {
                if (!a.date) return -1;
                if (!b.date) return 1;
                const da = typeof a.date === 'string' ? dayjs(a.date, 'DD-MMM-YYYY') : a.date;
                const db = typeof b.date === 'string' ? dayjs(b.date, 'DD-MMM-YYYY') : b.date;
                return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
              })
            );

            setSnackbar({ open: true, message: 'Saved!', severity: 'success' });

        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || err.message || 'Error',
                severity: 'error'
            });
        }

        if (isUnsaved) {
          setUnsavedRows(prev => prev.filter((_, i) => i !== idx));
          setUnsavedDateTouched(prev => prev.filter((_, i) => i !== idx));
        } else {
          setEditingIdx(null);
          setEditDateTouched(prev => {
            const copy = { ...prev };
            delete copy[idx];
            return copy;
          });
        }
    };

  // Delete a saved row
    async function handleDeleteSavedRow(idx: number) {
        if (!activity.engagementId) return;

        const row = rows[idx];
        if (!row.taskId) return;

        try {
            await benchActivityService.deleteTask(row.taskId);

            // 🔥 cast as any
            const tasks: any = await benchActivityService.getTasksByEngagementId(activity.engagementId);

            setRows(tasks.map((t: any) => ({
                taskId: t.taskID,
                date: t.taskDate ? dayjs(t.taskDate).format('DD-MMM-YYYY') : '',
                comments: t.comments,
                hours: t.hours?.toString() || ''
              })));

            setSnackbar({ open: true, message: 'Deleted!', severity: 'success' });

        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || err.message || 'Error',
                severity: 'error'
            });
        }

        if (editingIdx === idx) setEditingIdx(null);
    }

  /* ---------------- SUBMIT ---------------- */
  // Submit: update DB only with persisted rows
    const handleSubmit = async () => {
      // Prevent submit if status is 'yet to start'
      if (status === 'yet to start') {
        setSnackbar({ open: true, message: 'Please select a valid status before submitting.', severity: 'error' });
        return;
      }
      // Check for unsaved rows with missing fields
      if (unsavedRows.length > 0) {
        const hasEmptyUnsaved = unsavedRows.some(row => !row.date || !row.comments || !row.hours);
        if (hasEmptyUnsaved) {
          setSnackbar({
            open: true,
            message: 'All the fields are mandatory',
            severity: 'error',
          });
          return;
        }
        // Validate all unsaved rows are within allowed week
        const hasInvalidDate = unsavedRows.some(row => !isValidDate(row.date));
        if (hasInvalidDate) {
          setSnackbar({
            open: true,
            message: 'All dates must be within the current activity week.',
            severity: 'error',
          });
          return;
        }
      }
      // Check for at least one row
      if (!rows.length) {
        setSnackbar({
          open: true,
          message: 'Please add at least one task.',
          severity: 'error',
        });
        return;
      }
      // Check for empty fields in any saved row
      const hasEmpty = rows.some(row => {
        return !row.date || !row.comments || !row.hours;
      });
      if (hasEmpty) {
        setSnackbar({
          open: true,
          message: 'All the fields are mandatory',
          severity: 'error',
        });
        return;
      }
      // Validate all saved rows are within allowed week
      const hasInvalidSavedDate = rows.some(row => !isValidDate(row.date));
      if (hasInvalidSavedDate) {
        setSnackbar({
          open: true,
          message: 'All dates must be within the current activity week.',
          severity: 'error',
        });
        return;
      }
      try {
        if (activity.engagementId) {
          await benchActivityService.completeActivity(activity.engagementId);
        }
        setSnackbar({
          open: true,
          message: 'Update done successfully!',
          severity: 'success',
        });
        setTimeout(onClose, 1200);
      } catch (err: any) {
        setSnackbar({
          open: true,
          message: err?.response?.data?.message || err.message || 'Failed to update activity task.',
          severity: 'error',
        });
      }
    };

  // Custom onClose handler to only allow closing via close button
  const handleModalClose = (event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      // Prevent closing on backdrop or escape
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      disableEscapeKeyDown
    >
    <Box
      data-activity-week={activity.weekDisplay ? dayjs(activity.weekDisplay).format('DD-MMM-YYYY') : activity.week ? dayjs(activity.week).format('DD-MMM-YYYY') : ''}
      sx={{
        width: 820,
        bgcolor: '#fff',
        borderRadius: 3,
        boxShadow: 24,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxHeight: '75vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ================= HEADER ================= */}
      <Box px={2} pt={2} pb={0} display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize={19} fontWeight={700} color="#0B3A6A">
          Update Activity
        </Typography>
        <IconButton onClick={onClose}>
          <span style={{ fontSize: 28 }}>&times;</span>
        </IconButton>
      </Box>

      {/* ================= INFO BAR ================= */}
      <Box
        mx={2}
        mb={1.5}
        px={2}
        py={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}
      >
        <Box display="flex" alignItems="center" gap={1.4}>
          <Typography fontSize={12} fontWeight={600}>Week</Typography>
          <Typography fontSize={12} fontStyle="italic" color="#019CDC">
            {activity.weekDisplay
              ? dayjs(activity.weekDisplay).format('DD-MMM-YYYY')
              : activity.week
                ? dayjs(activity.week).format('DD-MMM-YYYY')
                : ''}
          </Typography>
          <Typography color="#aaa">|</Typography>
          <Typography fontSize={12} fontWeight={600}>Category</Typography>
          <Typography fontSize={12} fontStyle="italic" color="#019CDC">{activity.category}</Typography>
          <Typography color="#aaa">|</Typography>
          <Typography fontSize={12} fontWeight={600}>Technology</Typography>
          <Typography fontSize={12} fontStyle="italic" color="#019CDC">{activity.technology}</Typography>
          <Typography color="#aaa">|</Typography>
          <Typography fontSize={12} fontWeight={600}>Name</Typography>
          <Typography fontSize={12} fontStyle="italic" color="#019CDC">{activity.name}</Typography>
          <Select
            size="small"
            value={status}
            onChange={handleStatusChange}
            sx={{ ml: 1.5, fontSize: 12, height: 28 }}
          >
            <MenuItem value="Yet to start" disabled={status !== 'Yet to start'}>Yet to Start</MenuItem>
            <MenuItem value="In Progress">In-Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
          {/* Show Submit button only when status is 'completed' */}
          {status === "completed" && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{ minWidth: 96, background: '#124B84', fontSize: 12.5, ml: 2 }}
            >
              Submit
            </Button>
          )}
        </Box>
      </Box>

      {/* ================= TABLE ================= */}
      <TableContainer
        component={Paper}
        sx={{
          mx: 2,
          flex: 1,
          maxWidth: 'calc(100% - 32px)',
          overflowX: 'hidden',
        }}
      >
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'fixed',
          }}
        >
          <TableHead>
            <TableRow sx={{ background: '#0B3A6A' }}>
              <TableCell sx={{ width: '29%', fontSize: 13.5, fontWeight: 700, color: '#fff' }}>
                Date
              </TableCell>
              <TableCell sx={{ width: '66%', fontSize: 13.5, fontWeight: 700, color: '#fff' }}>
                Comments
              </TableCell>
              <TableCell sx={{ width: '15%', fontSize: 13.5, fontWeight: 700, color: '#fff' }}>
                Hours
              </TableCell>
              <TableCell
                align="center"
                sx={{ width: '15%', fontSize: 13.5, fontWeight: 700, color: '#fff' }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Persisted records (ticked rows) */}
            {rows.map((row, idx) => (
              <TableRow key={row.taskId ?? `row-${idx}`}> 
                {editingIdx === idx ? (
                  <>
                    <TableCell sx={{ py: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                        <TextField
                          size="small"
                          value={row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : ''}
                          placeholder="DD-MMM-YYYY"
                          onChange={e => {
                            let val = e.target.value;
                            // Only allow typing in DD-MMM-YYYY format as user types
                            // Allow partial input for user experience, but block invalid patterns
                            // Allow only digits, dash, and letters
                            if (/[^0-9A-Za-z-]/.test(val)) return;
                            // Only allow up to 2 digits, dash, 3 letters, dash, 4 digits
                            if (val.length > 11) return;
                            // Validate as user types
                            // 1-2 digits for day
                            if (val.length <= 2 && !/^([0-2]?\d|3[01])?$/.test(val)) return;
                            // 2 digits + dash
                            if (val.length === 3 && val[2] !== '-') return;
                            // 2 digits + dash + 1-3 letters
                            if (val.length > 3 && val.length <= 6 && !/^([0-2]?\d|3[01])\-[A-Za-z]{0,3}$/.test(val)) return;
                            // 2 digits + dash + 3 letters + dash
                            if (val.length === 7 && val[6] !== '-') return;
                            // 2 digits + dash + 3 letters + dash + 1-4 digits
                            if (val.length > 7 && !/^([0-2]?\d|3[01])\-[A-Za-z]{3}\-\d{0,4}$/.test(val)) return;
                            // If month is fully typed, check if it's a valid abbreviation
                            if (val.length >= 6) {
                              const month = val.substring(3, 6).toLowerCase();
                              const validMonths = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
                              if (val.length >= 6 && !validMonths.includes(month)) return;
                            }
                            // If day is fully typed, block 00 and >31
                            if (val.length >= 2) {
                              const day = val.substring(0, 2);
                              if (parseInt(day, 10) < 1 || parseInt(day, 10) > 31) return;
                            }
                            const updated = { ...row, date: val };
                            setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                          }}
                          sx={{ width: '100%', height: 36 }}
                          inputProps={{ pattern: '[0-9A-Za-z-]*', style: { fontSize: 14, padding: '0 8px', height: 36 } }}
                          error={commentsErrorIdx === idx || (!!row.date && (!isValidDate(row.date) || /[^0-9A-Za-z-]/.test(row.date?.toString() || '')))}
                          helperText={commentsErrorIdx === idx ? '' : (!!row.date && (!isValidDate(row.date) || /[^0-9A-Za-z-]/.test(row.date?.toString() || '')) ? 'Enter a valid date from calendar' : '')}
                        />
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                          onClick={e => {
                            setCalendarIdx(idx);
                            setCalendarType('edit');
                            setPendingDate(row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : null);
                            setCalendarOpen(true);
                          }}
                        >
                          <CalendarToday sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={4}
                        value={row.comments}
                        onChange={e => {
                          let value = e.target.value;
                          if (value.length > 800) {
                            setSnackbar({ open: true, message: 'You reached the character limit', severity: 'error' });
                            value = value.slice(0, 800);
                          }
                          const updated = { ...row, comments: value };
                          setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                        }}
                        inputProps={{ maxLength: 800, style: { fontSize: 15, padding: '0 4px', maxHeight: 36, overflowY: 'auto', boxSizing: 'border-box' } }}
                        sx={{
                          '& .MuiInputBase-input': {
                            overflowY: 'auto',
                            maxHeight: 36,
                            padding: '0 4px',
                            boxSizing: 'border-box',
                          },
                          height: 36,
                        }}
                        helperText={`${row.comments.length}/800 characters`}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                        <TextField
                          size="small"
                          type="text"
                          value={row.hours}
                          onKeyPress={e => {
                            const char = e.key;
                            const input = e.target as HTMLInputElement;
                            const value = input.value;
                            // Allow only digits and one dot
                            if (!/[0-9.]/.test(char)) {
                              e.preventDefault();
                              return;
                            }
                            // Prevent more than one dot
                            if (char === '.' && value.includes('.')) {
                              e.preventDefault();
                              return;
                            }
                            // Prevent more than 2 digits before decimal
                            if (char !== '.' && (!value.includes('.') && value.length >= 2)) {
                              if (!(input.selectionStart !== input.selectionEnd)) {
                                e.preventDefault();
                                return;
                              }
                            }
                            // Prevent more than 2 digits after decimal
                            if (value.includes('.')) {
                              const [before, after] = value.split('.');
                              if (char !== '.' && input.selectionStart !== null && input.selectionStart > value.indexOf('.') && after.length >= 2) {
                                if (!(input.selectionStart !== input.selectionEnd)) {
                                  e.preventDefault();
                                  return;
                                }
                              }
                            }
                            // Allow 0 as first digit if followed by dot (e.g., 0.5, 0.01)
                            if (value === '0' && char !== '.') {
                              e.preventDefault();
                              return;
                            }
                            let next = value.slice(0, input.selectionStart ?? 0) + char + value.slice(input.selectionEnd ?? 0);
                            if (next === '.') next = '0.';
                            const num = Number(next);
                            // Accept 0.01 to 10.0
                            if (next && (!/^([0-9]{1,2}|0)(\.[0-9]{0,2})?$/.test(next) || num < 0.01 || num > 10.0)) {
                              e.preventDefault();
                              return;
                            }
                          }}
                          onChange={e => {
                            let value = e.target.value;
                            // Allow empty string (user clearing input)
                            if (value === '') {
                              const updated = { ...row, hours: '' };
                              setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                              setHoursErrorIdx(null);
                              setHoursErrorMsg("");
                              setGlobalHoursError("");
                              return;
                            }
                            // Allow 0 as first digit if followed by dot (e.g., 0.5, 0.01)
                            // Remove leading zeros except for '0.'
                            value = value.replace(/^0+(?!\.)/, '');
                            if (value.startsWith('.')) value = '0' + value;
                            if (/^([0-9]{1,2}|0)(\.[0-9]{0,2})?$/.test(value)) {
                              // Accept 0.01 to 10.0
                              const num = Number(value);
                              if (num >= 0.01 && num <= 10.0) {
                                const updated = { ...row, hours: value };
                                setRows(prev => prev.map((r, i) => i === idx ? updated : r));
                                setHoursErrorIdx(null);
                                setHoursErrorMsg("");
                                setGlobalHoursError("");
                              }
                            }
                          }}
                          inputProps={{ inputMode: 'decimal', style: { fontSize: 15, textAlign: 'center', padding: '0 4px', height: 36 } }}
                          sx={{ height: 36 }}
                        />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                      <Tooltip title="Save Task" arrow>
                        <IconButton onClick={() => handleSaveEditRow(idx, row)}>
                          <CheckCircle sx={{ fontSize: 20, color: '#388e3c' }} />
                        </IconButton>
                      </Tooltip>
                      <IconButton onClick={() => handleDeleteSavedRow(idx)}>
                        <DeleteOutline sx={{ fontSize: 20, color: '#d32f2f' }} />
                      </IconButton>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ py: 0, height: 36, fontSize: 15 }}>
                      {row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : ''}
                    </TableCell>
                    <TableCell sx={{ py: 0, height: 36, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }}>
                      <Tooltip title={row.comments || ''} placement="top" arrow>
                        <span style={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                          {row.comments}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ py: 0, height: 36, fontSize: 15 }}>{row.hours}</TableCell>
                    <TableCell align="center" sx={{ py: 0, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', height: 36 }}>
                      <Tooltip title="Edit Task" arrow>
                        <IconButton onClick={() => handleEditRow(idx)}>
                          <Edit sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Task" arrow>
                        <IconButton onClick={() => handleDeleteSavedRow(idx)}>
                          <DeleteOutline sx={{ fontSize: 20, color: '#d32f2f' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
  

            {/* Unsaved rows (not persisted, disappear on close/refresh) */}
            {unsavedRows.map((row, idx) => (
              <TableRow key={row.taskId ?? `unsaved-${idx}`}> 
                <TableCell sx={{ py: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <TextField
                      size="small"
                      value={row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : ''}
                      placeholder="DD-MMM-YYYY"
                      onChange={e => {
                        let val = e.target.value;
                        // Only allow typing in DD-MMM-YYYY format as user types
                        if (/[^0-9A-Za-z-]/.test(val)) return;
                        if (val.length > 11) return;
                        if (val.length <= 2 && !/^([0-2]?\d|3[01])?$/.test(val)) return;
                        if (val.length === 3 && val[2] !== '-') return;
                        if (val.length > 3 && val.length <= 6 && !/^([0-2]?\d|3[01])\-[A-Za-z]{0,3}$/.test(val)) return;
                        if (val.length === 7 && val[6] !== '-') return;
                        if (val.length > 7 && !/^([0-2]?\d|3[01])\-[A-Za-z]{3}\-\d{0,4}$/.test(val)) return;
                        if (val.length >= 6) {
                          const month = val.substring(3, 6).toLowerCase();
                          const validMonths = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
                          if (val.length >= 6 && !validMonths.includes(month)) return;
                        }
                        if (val.length >= 2) {
                          const day = val.substring(0, 2);
                          if (parseInt(day, 10) < 1 || parseInt(day, 10) > 31) return;
                        }
                        const updated = { ...row, date: val };
                        setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                        setUnsavedDateTouched(prev => {
                          const arr = [...prev];
                          arr[idx] = false;
                          return arr;
                        });
                      }}
                      inputProps={{ pattern: '[0-9A-Za-z-]*', style: { fontSize: 12, padding: '0 8px', height: 36 } }}
                      sx={{ width: '100%', height: 36 }}
                      error={unsavedDateTouched[idx] && (!row.date || !isValidDate(row.date) || /[^0-9A-Za-z-]/.test(row.date?.toString() || ''))}
                      helperText={unsavedDateTouched[idx] && (!row.date || !isValidDate(row.date) || /[^0-9A-Za-z-]/.test(row.date?.toString() || '')) ? 'Enter a valid date from calendar' : ''}
                    />
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                      onClick={e => {
                        setCalendarIdx(idx);
                        setCalendarType('unsaved');
                        setPendingDate(row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : null);
                        setCalendarOpen(true);
                      }}
                    >
                      <CalendarToday sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={row.comments}
                    onChange={e => {
                      let value = e.target.value;
                      if (value.length > 800) {
                        setSnackbar({ open: true, message: 'You reached the character limit', severity: 'error' });
                        value = value.slice(0, 800);
                      }
                      const updated = { ...row, comments: value };
                      setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                    }}
                    inputProps={{ maxLength: 800, style: { fontSize: 15, padding: '0 4px', maxHeight: 36, overflowY: 'auto', boxSizing: 'border-box' } }}
                    sx={{
                      '& .MuiInputBase-input': {
                        overflowY: 'auto',
                        maxHeight: 36,
                        padding: '0 4px',
                        boxSizing: 'border-box',
                      },
                      height: 36,
                    }}
                    helperText={`${row.comments.length}/800 characters`}
                  />
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <TextField
                    size="small"
                    type="text"
                    value={row.hours}
                    onKeyPress={e => {
                      const char = e.key;
                      const input = e.target as HTMLInputElement;
                      const value = input.value;
                      if (!/[0-9.]/.test(char)) {
                        e.preventDefault();
                        return;
                      }
                      if (char === '.' && value.includes('.')) {
                        e.preventDefault();
                        return;
                      }
                      if (char !== '.' && (!value.includes('.') && value.length >= 2)) {
                        if (!(input.selectionStart !== input.selectionEnd)) {
                          e.preventDefault();
                          return;
                        }
                      }
                      if (value.includes('.')) {
                        const [before, after] = value.split('.');
                        if (char !== '.' && input.selectionStart !== null && input.selectionStart > value.indexOf('.') && after.length >= 2) {
                          if (!(input.selectionStart !== input.selectionEnd)) {
                            e.preventDefault();
                            return;
                          }
                        }
                      }
                      if (value === '0' && char !== '.') {
                        e.preventDefault();
                        return;
                      }
                      let next = value.slice(0, input.selectionStart ?? 0) + char + value.slice(input.selectionEnd ?? 0);
                      if (next === '.') next = '0.';
                      const num = Number(next);
                      if (next && (!/^([0-9]{1,2}|0)(\.[0-9]{0,2})?$/.test(next) || num < 0.01 || num > 10.0)) {
                        e.preventDefault();
                        return;
                      }
                    }}
                    onChange={e => {
                      let value = e.target.value;
                      // Allow empty string (user clearing input)
                      if (value === '') {
                        const updated = { ...row, hours: '' };
                        setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                        setHoursErrorIdx(null);
                        setHoursErrorMsg("");
                        setGlobalHoursError("");
                        return;
                      }
                      // Allow 0 as first digit if followed by dot (e.g., 0.5, 0.01)
                      // Remove leading zeros except for '0.'
                      value = value.replace(/^0+(?!\.)/, '');
                      if (value.startsWith('.')) value = '0' + value;
                      if (/^([0-9]{1,2}|0)(\.[0-9]{0,2})?$/.test(value)) {
                        // Accept 0.01 to 10.0
                        const num = Number(value);
                        if (num >= 0.01 && num <= 10.0) {
                          const updated = { ...row, hours: value };
                          setUnsavedRows(prev => prev.map((r, i) => i === idx ? updated : r));
                          setHoursErrorIdx(null);
                          setHoursErrorMsg("");
                          setGlobalHoursError("");
                        }
                      }
                    }}
                    inputProps={{ inputMode: 'decimal', style: { fontSize: 14, textAlign: 'center', padding: '0 4px', height: 36 } }}
                    sx={{ height: 36 }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ py: 1.5 }}>
                  <Tooltip title="Save Task" arrow>
                    <IconButton onClick={() => handleSaveEditRow(idx, row, true)}>
                      <CheckCircle sx={{ fontSize: 20, color: '#388e3c' }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {editRows.map((row, idx) => (
              <TableRow key={row.taskId ?? `edit-${idx}`} sx={{ height: 120 }}>
                <TableCell sx={{ py: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <TextField
                      size="small"
                      value={row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : ''}
                      placeholder="DD-MMM-YYYY"
                      onChange={e => {
                        let val = e.target.value;
                        if (/[^0-9A-Za-z-]/.test(val)) return;
                        if (val.length > 11) return;
                        if (val.length <= 2 && !/^([0-2]?\d|3[01])?$/.test(val)) return;
                        if (val.length === 3 && val[2] !== '-') return;
                        if (val.length > 3 && val.length <= 6 && !/^([0-2]?\d|3[01])\-[A-Za-z]{0,3}$/.test(val)) return;
                        if (val.length === 7 && val[6] !== '-') return;
                        if (val.length > 7 && !/^([0-2]?\d|3[01])\-[A-Za-z]{3}\-\d{0,4}$/.test(val)) return;
                        if (val.length >= 6) {
                          const month = val.substring(3, 6).toLowerCase();
                          const validMonths = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
                          if (val.length >= 6 && !validMonths.includes(month)) return;
                        }
                        if (val.length >= 2) {
                          const day = val.substring(0, 2);
                          if (parseInt(day, 10) < 1 || parseInt(day, 10) > 31) return;
                        }
                        const r = [...editRows];
                        r[idx].date = val;
                        setEditRows(r);
                        setEditDateTouched(touched => ({ ...touched, [idx]: false }));
                      }}
                      inputProps={{ pattern: '[0-9A-Za-z-]*', style: { fontSize: 12, padding: '0 8px' } }}
                      sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                          height: 30,
                          fontSize: 14,
                          px: 1,
                          py: 0,
                        },
                        '& input': {
                          fontSize: 12,
                          padding: '0 8px',
                        },
                      }}
                      error={editDateTouched[idx] && (!row.date || !isValidDate(row.date) || /[^0-9A-Za-z-]/.test(row.date?.toString() || ''))}
                      helperText={editDateTouched[idx] && (!row.date || !isValidDate(row.date) || /[^0-9A-Za-z-]/.test(row.date?.toString() || '')) ? 'Enter a valid date from calendar (no special characters)' : ''}
                    />
                    // Helper function to validate date (only allow dates from calendar)
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                      onClick={e => {
                        setCalendarIdx(idx);
                        setCalendarType('edit');
                        setPendingDate(row.date ? (typeof row.date === 'string' ? row.date : dayjs(row.date).format('DD-MMM-YYYY')) : null);
                        setCalendarOpen(true);
                      }}
                    >
                      <CalendarToday sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                  {/* Calendar Popper rendered at modal root for all edit rows */}
                </TableCell>

                <TableCell sx={{ py: 1.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={row.comments}
                    onChange={e => {
                      let value = e.target.value;
                      if (value.length > 800) {
                        setSnackbar({ open: true, message: 'You reached the character limit', severity: 'error' });
                        value = value.slice(0, 800);
                      }
                      const r = [...editRows];
                      r[idx].comments = value;
                      setEditRows(r);
                    }}
                    inputProps={{ maxLength: 800, style: { fontSize: 15, padding: '0 4px', maxHeight: 36, overflowY: 'auto', boxSizing: 'border-box' } }}
                    sx={{
                      '& .MuiInputBase-input': {
                        overflowY: 'auto',
                        maxHeight: 36,
                        padding: '0 4px',
                        boxSizing: 'border-box',
                      },
                      height: 36,
                    }}
                    helperText={`${row.comments.length}/800 characters`}
                  />
                </TableCell>

                <TableCell sx={{ py: 1.5 }}>
                  <TextField
                    size="small"
                    type="text"
                    value={row.hours}
                    onChange={e => {
                      let value = e.target.value;
                      // Allow empty string (user clearing input)
                      if (value === '') {
                        const r = [...editRows];
                        r[idx].hours = '';
                        setEditRows(r);
                        setHoursErrorIdx(null);
                        setHoursErrorMsg("");
                        setGlobalHoursError("");
                        return;
                      }
                      // Allow 0 as first digit if followed by dot (e.g., 0.5, 0.01)
                      // Remove leading zeros except for '0.'
                      value = value.replace(/^0+(?!\.)/, '');
                      if (value.startsWith('.')) value = '0' + value;
                      if (/^([0-9]{1,2}|0)(\.[0-9]{0,2})?$/.test(value)) {
                        // Accept 0.01 to 10.0
                        const num = Number(value);
                        if (num >= 0.01 && num <= 10.0) {
                          const r = [...editRows];
                          r[idx].hours = value;
                          setEditRows(r);
                          setHoursErrorIdx(null);
                          setHoursErrorMsg("");
                          setGlobalHoursError("");
                        }
                      }
                    }}
                    inputProps={{
                      inputMode: 'decimal',
                      style: {
                        fontSize: 14,
                        textAlign: 'center',
                        padding: '0 4px',
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 0,
                        fontSize: 15,
                        px: 0.5,
                        py: 0,
                      },
                    }}
                  />
                </TableCell>

                <TableCell align="center" sx={{ py: 1.5 }}>
                  <Tooltip title="Save Task" arrow>
                    <IconButton onClick={() => handleSaveEditRow(idx, editRows[idx])}>
                      <CheckCircle sx={{ fontSize: 20, color: '#388e3c' }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {!rows.length && !editRows.length && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ fontSize: 11.5, color: '#aaa' }}>
                  No tasks yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Display global hours error below the form */}
      {globalHoursError && (
        <div style={{ color: 'red', textAlign: 'center', marginTop: 8, fontSize: 15 }}>
          {globalHoursError}
        </div>
      )}

      {/* Move Add button below the table, only show if status is In Progress */}
      {status === "In Progress" && (
        <Box px={2} py={1} display="flex" justifyContent="flex-start">
          <Button
            onClick={handleAddRow}
            variant="contained"
            sx={{ minWidth: 96, minHeight: 20, fontSize: 12.5, background: '#124B84', '&:hover': { background: '#0B3A6A' } }}
          >
            + Add
          </Button>
        </Box>
      )}
      {/* Calendar Modal rendered at center of UpdateActivityModal */}
      <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)} maxWidth="xs" PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: 8,
          background: 'linear-gradient(135deg, #f8fafc 60%, #e3e9f3 100%)',
          minWidth: 320,
          maxWidth: 400,
          p: 0,
        }
      }}>
        <Box position="relative">
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={pendingDate && dayjs(pendingDate, 'DD-MMM-YYYY').isValid() ? dayjs(pendingDate, 'DD-MMM-YYYY') : null}
            onChange={date => {
              const formatted = date ? dayjs(date).format('DD-MMM-YYYY') : null;
              setPendingDate(formatted);
              // Immediately update the date field for unsaved or edit row
              if (calendarIdx !== null && formatted) {
                if (calendarType === 'unsaved') {
                  setUnsavedRows(prev => prev.map((r, i) => i === calendarIdx ? { ...r, date: formatted } : r));
                } else if (calendarType === 'edit') {
                  setEditRows(prev => prev.map((r, i) => i === calendarIdx ? { ...r, date: formatted } : r));
                }
                setCalendarOpen(false);
              }
            }}
            minDate={activity.week ? dayjs(activity.week) : undefined}
            maxDate={activity.week
              ? (dayjs(activity.week).add(6, 'day').isBefore(dayjs(), 'day') ? dayjs(activity.week).add(6, 'day') : dayjs())
              : undefined}
            shouldDisableDate={date => {
              // Disable if outside the activity's week (Monday to Sunday only)
              const weekStart = activity.week ? dayjs(activity.week) : null;
              const weekEnd = weekStart ? weekStart.add(6, 'day') : null;
              const today = dayjs();
              if (weekStart && weekEnd && (date.isBefore(weekStart, 'day') || date.isAfter(weekEnd, 'day') || date.isAfter(today, 'day'))) return true;
              // Disable if already used in rows (prevent duplicate)
              const usedDates = rows.map(r => dayjs(r.date).format('YYYY-MM-DD'));
              if (pendingDate) {
                // Allow selecting the current pending date (for editing)
                if (date.format('YYYY-MM-DD') === dayjs(pendingDate, 'DD-MMM-YYYY').format('YYYY-MM-DD')) return false;
              }
              return usedDates.includes(date.format('YYYY-MM-DD'));
            }}
            slotProps={{
              actionBar: { actions: [] },
              layout: { sx: { minWidth: 320, maxWidth: 400, fontSize: 13, p: 0 } },
            }}
            sx={{
              minWidth: 320,
              maxWidth: 400,
              fontSize: 13,
              borderRadius: 2,
              boxShadow: 2,
              background: '#f4f7fb',
              p: 0,
              '.MuiPickersLayout-root': {
                minHeight: 240,
                maxHeight: 270,
                p: 0,
              },
              '.MuiPickersCalendarHeader-root': {
                background: 'rgba(25, 118, 210, 0.07)',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              },
              '.MuiPickersDay-root': {
                fontWeight: 500,
                fontSize: 12,
                borderRadius: 6,
                transition: 'background 0.2s',
              },
              '.Mui-selected': {
                background: '#1976d2 !important',
                color: '#fff !important',
              },
              '.MuiPickersDay-root:hover': {
                background: '#e3e9f3',
              },
            }}
          />
          <Box position="absolute" bottom={28} right={18} display="flex" gap={2}>
            
            <span
              style={{
                cursor: 'pointer',
                color: '#888',
                fontWeight: 500,
                fontSize: 13,
                letterSpacing: 0.2,
                padding: '2px 10px',
                borderRadius: 6,
                transition: 'background 0.2s',
              }}
              onClick={() => setCalendarOpen(false)}
              onMouseOver={e => (e.currentTarget.style.background = '#f0f0f0')}
              onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
            >Cancel</span>
          </Box>
        </Box>
      </Dialog>

      {/* ================= SUBMIT ================= */}
      {/* Submit button moved to header, see above */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  </Modal>
);
}